import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Card, Progress, Divider, Button, message } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  SafetyOutlined,
  LockOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useInscription } from "../../hooks/useInscription";
import {
  PersonalInfoStep,
  PhoneVerificationStep,
  OTPVerificationStep,
  PasswordStep,
  TermsStep,
} from "../steps/StepComponents";
import { useNavigate, useParams } from "react-router-dom";
import { validateStep, ValidationResult } from "../../utils/validation";

const { Text } = Typography;

const Inscription: React.FC = () => {
  const { code, entrepriseId } = useParams<{
    code?: string;
    entrepriseId?: string;
  }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    currentStep,
    loading,
    formData,
    isOtpVerified,
    actualOTP,
    updateFormField,
    sendVerificationCode,
    verifyOtpCode,
    sendEmailOTP,
    handleNext,
    acceptTermsAndProceed,
  } = useInscription();
  const isRTL = i18n.language === "ar";
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (entrepriseId) localStorage.setItem("entrepriseId", entrepriseId);
  if (code) localStorage.setItem("code", code);
  else console.warn("No code param in URL");

  const handleFieldChangeWrapper = (
    field: keyof typeof formData,
    value: any
  ) => {
    updateFormField(field, value);
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const handleNextWrapper = async () => {
    if (loading) return;
    const validation: ValidationResult = validateStep(currentStep, formData, t);
    if (!validation.isValid) {
      setErrors(validation.errors);
      const firstMsg = Object.values(validation.errors)[0];
      message.error(firstMsg);
      return;
    }
    setErrors({});
    await handleNext();
  };

  const steps = [
    {
      title: t("inscription.step0.title") || "Conditions d'utilisation",
      icon: <FileTextOutlined />,
      description:
        t("inscription.step0.description") || "Accepter les conditions",
      number: 1,
    },
    {
      title: t("inscription.step1.title"),
      icon: <UserOutlined />,
      description: t("inscription.step1.description"),
      number: 2,
    },
    {
      title: t("inscription.step2.title"),
      icon: <PhoneOutlined />,
      description: t("inscription.step2.description"),
      number: 3,
    },
    {
      title: t("inscription.step3.title"),
      icon: <SafetyOutlined />,
      description: t("inscription.step3.description"),
      number: 4,
    },
    {
      title: t("inscription.step4.title"),
      icon: <LockOutlined />,
      description: t("inscription.step4.description"),
      number: 5,
    },
  ];

  const renderStepContent = () => {
    const commonProps = {
      formData,
      loading,
      isRTL,
      onFieldChange: handleFieldChangeWrapper,
      onNext: handleNextWrapper,
      errors,
    } as any;

    switch (currentStep) {
      case 0:
        return <TermsStep {...commonProps} onAccept={acceptTermsAndProceed} />;
      case 1:
        return <PersonalInfoStep {...commonProps} />;
      case 2:
        return (
          <PhoneVerificationStep
            {...commonProps}
            onSendOTP={sendVerificationCode}
          />
        );
      case 3:
        return (
          <OTPVerificationStep
            {...commonProps}
            isOtpVerified={isOtpVerified}
            onVerifyOTP={verifyOtpCode}
            onSendEmailOTP={(email: string) =>
              sendEmailOTP(email, formData.phoneNumber, actualOTP)
            }
          />
        );
      case 4:
        return <PasswordStep {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        maxWidth: "100%",
        padding: 20,
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          maxWidth: 500,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <h2>{t("inscription.newTitle")}</h2>
        </div>
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("inscription.stepCounter.currentStep", {
              currentStep: currentStep + 1,
              totalSteps: steps.length,
            })}
          </Text>
          <Progress
            percent={(currentStep / (steps.length - 1)) * 100}
            showInfo={false}
            strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }}
            trailColor="#f0f0f0"
            strokeWidth={8}
          />
        </Card>

        <Card
          style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
        >
          <div style={{ marginBottom: 32 }}>
            {steps.map((step, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  marginBottom: index === steps.length - 1 ? 0 : 24,
                  gap: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 30,
                    order: isRTL ? 2 : 1,
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      backgroundColor:
                        index < currentStep
                          ? "#52c41a"
                          : index === currentStep
                          ? "#1890ff"
                          : "#fff",
                      border: `2px solid ${
                        index < currentStep
                          ? "#52c41a"
                          : index === currentStep
                          ? "#1890ff"
                          : "#d9d9d9"
                      }`,
                      color: index <= currentStep ? "#fff" : "#999",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: "bold",
                      zIndex: 2,
                    }}
                  >
                    {index < currentStep ? "✓" : step.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      style={{
                        width: 2,
                        height: index === currentStep ? 120 : 60,
                        backgroundColor:
                          index < currentStep ? "#52c41a" : "#d9d9d9",
                        marginTop: 8,
                      }}
                    />
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    order: isRTL ? 1 : 2,
                    [isRTL ? "paddingRight" : "paddingLeft"]: 16,
                  }}
                >
                  <div
                    style={{
                      marginBottom: index === currentStep ? 16 : 0,
                      backgroundColor:
                        index === currentStep
                          ? "rgba(24, 144, 255, 0.03)"
                          : "transparent",
                      padding: index === currentStep ? "12px" : "0",
                      borderRadius: index === currentStep ? "8px" : "0",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: index === currentStep ? 600 : 500,
                        color:
                          index === currentStep
                            ? "#000"
                            : index < currentStep
                            ? "#52c41a"
                            : "#666",
                        marginBottom: 4,
                      }}
                    >
                      {step.title}
                    </div>
                    <div style={{ fontSize: 12, color: "#999" }}>
                      {step.description}
                    </div>
                  </div>

                  {index === currentStep && (
                    <div style={{ marginTop: 8 }}>{renderStepContent()}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div
          style={{
            textAlign: "center",
            marginTop: 24,
            padding: 16,
          }}
        />
      </div>
    </div>
  );
};

export default Inscription;
